import { useState } from "react";
import PropTypes from "prop-types";
import { useIntl } from "react-intl";
import CategoryTag from "core/tags/category-tag/CategoryTag";
import { splitKeywords } from "utils/fafHelper";

// Keyword field as "chips": type a word then Enter (or comma) to
// create a tag; ×/Backspace (empty field) removes it. The value stays a
// comma-separated string (back contract unchanged: toNeedPayload splits on ",").
const FafTagsInput = ({ id, value, onChange, placeholder, dataTestId }) => {
  const intl = useIntl();
  const [input, setInput] = useState("");
  const tags = splitKeywords(value);
  // Test id shared by the input and its chips (falls back to `id` when unset).
  const testId = dataTestId || id;

  // Adds a list of words (deduplicated, case-insensitive) in a single onChange.
  const addTags = (rawList) => {
    const seen = new Set(tags.map((t) => t.toLowerCase()));
    const next = [...tags];
    rawList.forEach((raw) => {
      const tag = raw.trim();
      if (tag && !seen.has(tag.toLowerCase())) {
        seen.add(tag.toLowerCase());
        next.push(tag);
      }
    });
    if (next.length !== tags.length) onChange(next.join(","));
  };

  const removeTag = (index) =>
    onChange(tags.filter((_, i) => i !== index).join(","));

  // Commits the current text as a tag (used by Enter and by focus loss).
  const commitInput = () => {
    if (input.trim()) {
      addTags([input]);
      setInput("");
    }
  };

  // A typed (or pasted) comma splits: everything but the last fragment becomes tags.
  const handleChange = (e) => {
    const raw = e.target.value;
    if (raw.includes(",")) {
      const parts = raw.split(",");
      const last = parts.pop();
      addTags(parts);
      setInput(last);
    } else {
      setInput(raw);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      commitInput();
    } else if (e.key === "Backspace" && !input && tags.length) {
      removeTag(tags.length - 1);
    }
  };

  return (
    <div className="faf-tags-input">
      {tags.map((tag, i) => (
        <CategoryTag
          key={tag}
          label={tag}
          deleteOption
          onDelete={() => removeTag(i)}
          dataTestId={`${testId}-tag-${i}`}
        />
      ))}
      <input
        id={id}
        data-testid={testId}
        className="faf-tags-input-field"
        value={input}
        placeholder={
          tags.length
            ? ""
            : placeholder ||
              intl.formatMessage({
                id: "tools_manager_faf_need_keywords_ph",
                defaultMessage: "Un mot-clé puis Entrée ou Virgule",
              })
        }
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={commitInput}
      />
    </div>
  );
};

FafTagsInput.propTypes = {
  id: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  dataTestId: PropTypes.string,
};

export default FafTagsInput;
